
import { Facebook, Mail, Link2, Send, MessageCircle, X, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

// Custom X (Twitter) icon that matches the new branding
const XIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M4 4l11.5 11.5M4 20l15-15" />
  </svg>
);

// Custom WhatsApp icon since it's not in the core lucide-react package
const WhatsApp = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M3 21l1.65-3.8a9 9 0 1 1 3.4 2.9L3 21" />
    <path d="M9 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0v1Z" />
    <path d="M14 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0v1Z" />
    <path d="M9.5 13.5h5" />
  </svg>
);

// Custom TikTok icon with proper TikTok logo design
const TikTok = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M9 12a4 4 0 1 0 4 4V8a5 5 0 0 0 5 5" />
  </svg>
);

// Custom Instagram icon with proper design
const Instagram = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <rect width="20" height="20" x="2" y="2" rx="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
  </svg>
);

interface ShareButtonsProps {
  postId: string;
  content: string;
  isCompact?: boolean;
}

export const ShareButtons = ({ postId, content, isCompact = false }: ShareButtonsProps) => {
  const { toast } = useToast();
  const baseUrl = window.location.origin;
  const postUrl = `${baseUrl}/post/${postId}`;
  const encodedUrl = encodeURIComponent(postUrl);
  const encodedText = encodeURIComponent(content.slice(0, 200) + (content.length > 200 ? '...' : ''));
  const [isExpanded, setIsExpanded] = useState(false);

  const trackShare = async (shareType: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.from('post_shares').insert({
          post_id: postId,
          user_id: user.id,
          share_type: shareType
        });
      }
    } catch (error) {
      console.error('Error tracking share:', error);
    }
  };

  const preventBubbling = (e: React.MouseEvent, callback: () => void) => {
    e.preventDefault();
    e.stopPropagation();
    callback();
  };

  const handleTwitterShare = async () => {
    await trackShare('twitter');
    window.open(`https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`, '_blank');
  };

  const handleFacebookShare = async () => {
    await trackShare('facebook');
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`, '_blank');
  };

  const handleInstagramShare = async () => {
    await trackShare('instagram');
    try {
      await navigator.clipboard.writeText(postUrl);
      
      const instagramUrl = `instagram://camera`;
      const instagramWebUrl = `https://www.instagram.com/`;
      
      window.location.href = instagramUrl;
      
      setTimeout(() => {
        if (window.location.href === instagramUrl) {
          window.open(instagramWebUrl, '_blank');
        }
      }, 500);
      
      toast({
        title: "Instagram Sharing",
        description: "Link copied! Open Instagram and paste it in your story or post.",
      });
    } catch (error) {
      console.error('Error with Instagram share:', error);
      toast({
        title: "Sharing Error",
        description: "Could not prepare Instagram share. Link has been copied to clipboard.",
        variant: "destructive"
      });
    }
  };

  const handleTikTokShare = async () => {
    await trackShare('tiktok');
    try {
      await navigator.clipboard.writeText(postUrl);
      
      const tiktokUrl = `tiktok://`;
      const tiktokWebUrl = `https://www.tiktok.com/upload`;
      
      window.location.href = tiktokUrl;
      
      setTimeout(() => {
        if (window.location.href === tiktokUrl) {
          window.open(tiktokWebUrl, '_blank');
        }
      }, 500);
      
      toast({
        title: "TikTok Sharing",
        description: "Link copied! Open TikTok and paste it in your video description.",
      });
    } catch (error) {
      console.error('Error with TikTok share:', error);
      toast({
        title: "Sharing Error",
        description: "Could not prepare TikTok share. Link has been copied to clipboard.",
        variant: "destructive"
      });
    }
  };

  const handleWhatsAppShare = async () => {
    await trackShare('whatsapp');
    window.open(`https://wa.me/?text=${encodedText}%20${encodedUrl}`, '_blank');
  };

  const handleMessengerShare = async () => {
    await trackShare('messenger');
    window.open(`https://www.facebook.com/dialog/send?link=${encodedUrl}&app_id=184683071273&redirect_uri=${encodedUrl}`, '_blank');
  };

  const handleSendMessage = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please sign in to send messages",
          variant: "destructive"
        });
        return;
      }

      const { error } = await supabase.from('direct_messages').insert({
        sender_id: user.id,
        content: `Check out this post: ${postUrl}\n\n${content}`,
        is_read: false
      });

      if (error) throw error;

      await trackShare('direct_message');
      
      toast({
        title: "Message Sent!",
        description: "The post has been shared via direct message.",
      });
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Error",
        description: "Could not send the message. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleEmailShare = async () => {
    await trackShare('email');
    window.open(`mailto:?subject=Check out this post&body=${encodedText}%0A%0A${encodedUrl}`);
  };

  const handleCopyLink = async () => {
    await trackShare('link');
    try {
      await navigator.clipboard.writeText(postUrl);
      toast({
        title: "Link Copied!",
        description: "Post link has been copied to your clipboard.",
        variant: "default",
        duration: 3000,
      });
    } catch (error) {
      console.error('Error copying link:', error);
      toast({
        title: "Error",
        description: "Could not copy link. Please try again.",
        variant: "destructive",
        duration: 3000,
      });
    }
  };

  const toggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsExpanded(!isExpanded);
  };

  // Expandable share button for the main view
  if (!isCompact) {
    return (
      <div className="relative" onClick={(e) => e.stopPropagation()}>
        <Button
          variant="ghost"
          size="sm"
          className="rounded-full px-2 py-1 hover:bg-transparent hover:text-primary"
          onClick={toggleExpand}
          aria-label="Share"
        >
          <Share2 className="w-4 h-4" />
        </Button>
        
        {/* Expandable icons */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div 
              className="absolute bottom-10 right-0 bg-white rounded-full shadow-lg p-2 flex gap-2 z-10"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Button
                variant="ghost"
                size="sm"
                className="rounded-full p-2 hover:bg-gray-100"
                onClick={(e) => preventBubbling(e, handleTwitterShare)}
                aria-label="Share on X (Twitter)"
              >
                <XIcon className="w-4 h-4" />
              </Button>

              <Button
                variant="ghost"
                size="sm"
                className="rounded-full p-2 hover:bg-gray-100"
                onClick={(e) => preventBubbling(e, handleFacebookShare)}
                aria-label="Share on Facebook"
              >
                <Facebook className="w-4 h-4 text-[#1877F2]" />
              </Button>

              <Button
                variant="ghost"
                size="sm"
                className="rounded-full p-2 hover:bg-gray-100"
                onClick={(e) => preventBubbling(e, handleWhatsAppShare)}
                aria-label="Share on WhatsApp"
              >
                <WhatsApp className="w-4 h-4 text-[#25D366]" />
              </Button>

              <Button
                variant="ghost"
                size="sm"
                className="rounded-full p-2 hover:bg-gray-100"
                onClick={(e) => preventBubbling(e, handleCopyLink)}
                aria-label="Copy link"
              >
                <Link2 className="w-4 h-4" />
              </Button>

              <Button
                variant="ghost"
                size="sm"
                className="rounded-full p-2 hover:bg-gray-100"
                onClick={(e) => preventBubbling(e, handleSendMessage)}
                aria-label="Send as message"
              >
                <Send className="w-4 h-4" />
              </Button>

              <Button
                variant="ghost"
                size="sm"
                className="rounded-full p-2 hover:bg-gray-100"
                onClick={toggleExpand}
                aria-label="Close share menu"
              >
                <X className="w-4 h-4" />
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Original compact layout (popup menu)
  return (
    <div 
      className="p-4 flex flex-col gap-4 min-w-[240px] bg-white"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="grid grid-cols-3 gap-3">
        <Button 
          variant="outline" 
          size="icon"
          className="rounded-full border-2 border-gray-200 bg-white hover:bg-gray-50 p-2"
          onClick={(e) => preventBubbling(e, handleTwitterShare)}
          aria-label="Share on X (Twitter)"
          title="Share on X"
        >
          <XIcon className="h-5 w-5 text-black" />
        </Button>
        
        <Button 
          variant="outline" 
          size="icon"
          className="rounded-full border-2 border-gray-200 bg-white hover:bg-gray-50 p-2"
          onClick={(e) => preventBubbling(e, handleFacebookShare)}
          aria-label="Share on Facebook"
          title="Share on Facebook"
        >
          <Facebook className="h-5 w-5 text-[#1877F2]" />
        </Button>
        
        <Button 
          variant="outline" 
          size="icon"
          className="rounded-full border-2 border-gray-200 bg-white hover:bg-gray-50 p-2"
          onClick={(e) => preventBubbling(e, handleInstagramShare)}
          aria-label="Share on Instagram"
          title="Share on Instagram"
        >
          <Instagram className="h-5 w-5 text-[#E4405F]" />
        </Button>
        
        <Button 
          variant="outline" 
          size="icon"
          className="rounded-full border-2 border-gray-200 bg-white hover:bg-gray-50 p-2"
          onClick={(e) => preventBubbling(e, handleTikTokShare)}
          aria-label="Share on TikTok"
          title="Share on TikTok"
        >
          <TikTok className="h-5 w-5 text-black" />
        </Button>

        <Button 
          variant="outline" 
          size="icon"
          className="rounded-full border-2 border-gray-200 bg-white hover:bg-gray-50 p-2"
          onClick={(e) => preventBubbling(e, handleWhatsAppShare)}
          aria-label="Share on WhatsApp"
          title="Share on WhatsApp"
        >
          <WhatsApp className="h-5 w-5 text-[#25D366]" />
        </Button>

        <Button 
          variant="outline" 
          size="icon"
          className="rounded-full border-2 border-gray-200 bg-white hover:bg-gray-50 p-2"
          onClick={(e) => preventBubbling(e, handleMessengerShare)}
          aria-label="Share via Messenger"
          title="Share via Messenger"
        >
          <MessageCircle className="h-5 w-5 text-[#0078FF]" />
        </Button>
        
        <Button 
          variant="outline" 
          size="icon"
          className="rounded-full border-2 border-gray-200 bg-white hover:bg-gray-50 p-2"
          onClick={(e) => preventBubbling(e, handleEmailShare)}
          aria-label="Share via Email"
          title="Share via Email"
        >
          <Mail className="h-5 w-5 text-gray-700" />
        </Button>
        
        <Button 
          variant="outline" 
          size="icon"
          className="rounded-full border-2 border-gray-200 bg-white hover:bg-gray-50 p-2"
          onClick={(e) => preventBubbling(e, handleCopyLink)}
          aria-label="Copy link to clipboard"
          title="Copy link"
        >
          <Link2 className="h-5 w-5 text-gray-700" />
        </Button>
      </div>
      
      <Button 
        variant="outline" 
        size="sm"
        className="w-full rounded-md mt-1 flex items-center justify-start gap-2 bg-white hover:bg-gray-50 border-2 border-gray-200"
        onClick={(e) => preventBubbling(e, handleSendMessage)}
        aria-label="Send as Direct Message"
        title="Send as Direct Message"
      >
        <Send className="h-5 w-5 mr-2" />
        Send as Message
      </Button>
    </div>
  );
};
